import { FieldInfo } from '@/components/FieldInfo';
import { handleStudyListCacheUpdate } from '@/config/studies/editInPlace';
import { extractFieldErrors } from '@/lib/api-errors';
import { applyServerFieldErrors, clearServerFieldErrors } from '@/lib/form-errors';
import { mapFieldErrorsToForm } from '@/lib/map-field-errors';
import { updateStudy } from '@/services/studies';
import type { Study } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import createStyles from '../../../components/Create/styles.module.css';

interface EditStudyFormProps {
  study: Study;
  orgId: string;
  /** Called with the unwrapped, updated Study on success. */
  onSuccess: (updated: Study) => void;
}

/**
 * Form for editing a study's name and description.
 * Follows the same pattern as CreateForm — @tanstack/react-form + useMutation + toast.promise.
 * Only study owners can submit; the server will return 403 otherwise.
 */
export const EditStudyForm: FC<EditStudyFormProps> = ({ study, orgId, onSuccess }) => {
  const { t } = useTranslation('studies');
  const { t: tGeneral } = useTranslation();
  const queryClient = useQueryClient();

  const nameSchema = z
    .string()
    .transform(s => s.trim())
    .pipe(
      z
        .string()
        .min(3, t('study.validation.name_min_length'))
        .refine(s => !s.includes("'"), t('study.validation.name_no_apostrophe')),
    );

  // Description allows empty strings on edit (unlike CreateForm which requires .min(1)).
  // The API uses PATCH semantics: sending description: "" clears the field.
  const descriptionSchema = z.string();

  const mutation = useMutation({
    mutationFn: async (values: { name: string; description: string }): Promise<Study> => {
      const result = await updateStudy(orgId, study.id, {
        name: values.name.trim() !== study.name ? values.name.trim() : undefined,
        description: values.description.trim() !== (study.description ?? '') ? values.description.trim() : undefined,
      });
      // Unwrap the response wrapper — callers and cache updates expect the plain Study object.
      return result!.study;
    },
    onSuccess: (updatedStudy, variables) => {
      // Direct cache update — matches the established pattern in editInPlace.ts.
      // Avoids an unnecessary network round-trip compared to invalidateQueries + refetch.
      handleStudyListCacheUpdate(orgId, study.id)(queryClient, updatedStudy, variables);
    },
    onError: error => {
      const fieldErrors = extractFieldErrors(error);
      const { formErrors, unmapped } = mapFieldErrorsToForm(fieldErrors, { arrayFields: [] });
      // Generic string `detail`/`message` responses land in `unmapped`. The deleted
      // remap-server-field-errors helper funneled these onto the name field so they stayed
      // visible; preserve that so a server message can't fail silently. (The toast.promise
      // error below shows a generic failure; this keeps the specific message visible inline.)
      if (unmapped.length > 0 && formErrors.name === undefined) {
        formErrors.name = unmapped[0];
      }
      applyServerFieldErrors(form, formErrors);
    },
  });

  const form = useForm({
    defaultValues: {
      name: study.name,
      description: study.description ?? '',
    },
    onSubmit: async ({ value }) => {
      clearServerFieldErrors(form);

      const promise = mutation.mutateAsync(value);

      toast.promise(promise, {
        loading: t('study.edit_saving'),
        success: () => t('study.edit_success', { name: value.name }),
        error: () => t('study.edit_failed'),
      });

      const updatedStudy = await promise;

      form.reset();
      onSuccess(updatedStudy);
    },
  });

  return (
    <div className={createStyles.formContainer}>
      <form
        className={createStyles.form}
        onSubmit={async e => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          validators={{
            onChange: nameSchema,
            onMount: nameSchema,
          }}
        >
          {field => (
            <>
              <Input
                autoComplete="off"
                // oxlint-disable-next-line no-autofocus
                autoFocus
                className={createStyles.uiInput}
                data-testid="edit-study-name-input"
                id={field.name}
                label={{ children: tGeneral('general:name'), htmlFor: field.name, id: `label-${field.name}` }}
                name={field.name}
                onBlur={field.handleBlur}
                onChange={e => field.handleChange(e.target.value)}
                size="xl"
                value={field.state.value}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>

        <form.Field
          name="description"
          validators={{
            onChange: descriptionSchema,
          }}
        >
          {field => (
            <>
              <Textarea
                autoComplete="off"
                className={createStyles.uiInput}
                data-testid="edit-study-description-input"
                id={field.name}
                label={{ children: tGeneral('general:desc'), htmlFor: field.name, id: `label-${field.name}` }}
                name={field.name}
                onBlur={field.handleBlur}
                onChange={({ target }: { target: { value: string } }) => field.handleChange(target.value)}
                rows={5}
                size="xl"
                value={field.state.value}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>

        <form.Subscribe
          selector={state => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
            isSubmitted: state.isSubmitted,
          })}
        >
          {({ canSubmit, isSubmitting, isSubmitted }) => (
            <div className={createStyles.footer}>
              <Button
                className={isSubmitting || isSubmitted ? createStyles.noPointer : undefined}
                data-testid="edit-study-submit-btn"
                isDisabled={!canSubmit || isSubmitting}
                size="xl"
                type="submit"
                visual={isSubmitting || isSubmitted ? 'success' : 'primary'}
              >
                {isSubmitting || isSubmitted ? (
                  <>
                    <Loader className={createStyles.spinner} />
                    &nbsp;{tGeneral('general:submitting')}
                  </>
                ) : (
                  tGeneral('general:save')
                )}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form>
    </div>
  );
};

EditStudyForm.displayName = 'EditStudyForm';
